import * as React from 'react';

/**
 * DataGrid renderer type
 * @typedef {Object} DataGridRenderer
 * @property {string} name - The name of this renderer
 * @property {function(!Array<*>, !Object, !CloudObject, !BrickContext, !DataGridConfig, function(), function()):ReactElement} render
 *      - The render method with the following parameters:
 *          1. an array of the mapped properties as values
 *          2. an options object
 *          3. the current row instance
 *          4. the data grid execution context
 *          5. the current column config
 *          6. the function invalidating specific columns for the row.
 *          7. setNewCellValue, setting a new value for the current cell (& forcing re-rendering)
 * @property {function(Object, CloudObject, !Property, BrickContext, Object, Object, function()):ReactElement} renderForEdition
 * The renderForEdition method with the following parameters:
 *          1. the current value of the mapped property
 *          2. the current row instance (object whose property is updated)
 *          3. the current property being updated
 *          4. the datagrid execution context
 *          5. the renderer options (as in the DG config)
 *          6. the column config (DataGridConfig prop tag -> value)
 *          7. the function invalidating specific columns for the row.
 **/
/**
 * DataGrid sorterer type
 * @typedef {Object} DataGridSorterer
 * @property {string} name
 * @property {function(*,*,!BrickContext,!CloudObject,!CloudObject):number } sort
 *         -The sort method with the following parameters:
 *            1.first value to be compared which is of any type like number,string,date
 *            2.second value to be compared which is of any type like number,string,date
 *            3.the data grid execution context
 *            4.Cloud object to compare in case of BCPworkflow sorting
 *            5.Cloud object to compare in case of BCPworkflow sorting
 *

 }}
 */

/**
 * DataGrid registry type
 */
class DataGridRegistry {
    constructor() {
        /**
         * @type {!Map<string, !DataGridRenderer>}
         */
        this.renderers = new Map();
        /**
         * @type {!Map<string, !DataGridSorterer>}
         */
        this.sorterers= new Map();
    }
}

/**
 * DataGrid registry singleton
 */
const dataGridRegistry = new DataGridRegistry();

/**
 * Register a new renderer in the DataGrid registry
 * @param {!DataGridRenderer} renderer
 */
export function registerDataGridRenderer(renderer) {
    dataGridRegistry.renderers.set(renderer.name, renderer);
}
/**
 * Register a new sorterer in the DataGrid registry
 * @param {!DataGridSorterer} sorterer
 */
export function registerDataGridSorterer(sorterer) {
    dataGridRegistry.sorterers.set(sorterer.name, sorterer);
}

/**
 * Get a renderer from the DataGrid registry
 * @param {string} name
 * @return {DataGridRenderer}
 */
export function getDataGridRenderer(name) {
    return dataGridRegistry.renderers.get(name) || dataGridRegistry.renderers.get('DEFAULT');
}

/**
 * Get a sorterer from the DataGrid registry
 * it will return sorterer based on the name of sorterer type 
 * if sorterer type is null or undefined it will return default sorterer so will not sort
 * @param {string} name
 * @return {DataGridSorterer}
 */

export function getDataGridSorterer(name) {
    const sorterer = dataGridRegistry.sorterers.get(name);
    if (sorterer === null || sorterer === undefined) {
        console.error(`Sorter with type: "${name}" not found. Please check your DataGrid Config. No sorting will be applied`);
        // default sorterer is always returning 0 thus not sorting.
        return dataGridRegistry.sorterers.get('DEFAULT');
    } else {
        return sorterer;
    }

}

/**
 * DEFAULT
 * Default renderer
 */
registerDataGridRenderer({
    name: 'DEFAULT',
    render: () => {
        return (<p>{'INVALID RENDERER'}</p>);
    },
    renderForEdition: () => {
        return (<p>{'INVALID EDIT RENDERER'}</p>);
    }
});

/**
 * DEFAULT
 * Default sorterer
 */

registerDataGridSorterer({
    name: 'DEFAULT',
    sort:()=>{
        return 0;
       }
});
